import Select from "../../components/ui/Select";
import Pagination from "../../components/ui/Pagination";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Reuse the Products page styles (tables, tabs, dropdowns, pagination)
import "../ProductsPage/ProductsPage.css";

import {
  getAllVendorProducts,
  getVendors,
  createVendorProduct,
  updateVendorProduct,
  updateProductPricing,
  setProductFeatured,
  deleteVendorProduct,
  getApiErrorMessage,
} from "../../services/vendorService";

import { Vendor, VendorProduct, ProductCategory } from "../../types/vendor";

import AddProductModal from "../../compoonents/AddProductModal/AddProductModal";
import EditProductModal from "../../compoonents/EditProductModal/EditProductModal";
import UpdatePricingModal from "../../compoonents/UpdatePricingModal/UpdatePricingModal";
import DeleteConfirmationModal from "../../compoonents/DeleteConfirmationModal/DeleteConfirmationModal";
import ProductImage from "../../compoonents/ProductImage/ProductImage";
import PageLoader from "../../compoonents/PageLoader";
import {
  PageFilterDropdown,
  PageSearchBar,
  PageToolbar,
} from "../../compoonents/PageToolbar";

import { FiStar, FiTag, FiEdit2, FiTrash2 } from "react-icons/fi";

import defaultProductImage from "../../assets/images/empty.png";
import {
  PRODUCT_STATUS_OPTIONS,
  formatProductStatus,
  normalizeProductStatusForApi,
} from "../../constants/productStatus";

type NaijaShopTab = "products" | "orders";

type ProductUpdateData = Partial<
  Pick<VendorProduct, "name" | "description" | "quantity" | "productStatus">
>;
type PricingUpdateData = { priceNGN?: number; priceGBP?: number };

const CATEGORIES: { value: ProductCategory; label: string }[] = [
  { value: "clothes", label: "Clothes" },
  { value: "shoes", label: "Shoes" },
  { value: "bags", label: "Bags" },
  { value: "household", label: "Household" },
  { value: "kids", label: "Kids" },
  { value: "baby", label: "Baby" },
  { value: "food", label: "Food" },
];

const formatCategory = (category: ProductCategory | string) => {
  if (!category) return "N/A";
  return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
};

const vendorNameOf = (
  product: VendorProduct,
  vendorMap: Record<string, string>,
) => {
  const anyP = product as any;
  return (
    anyP.vendor?.name ||
    (product.vendorId ? vendorMap[product.vendorId] : undefined) ||
    "—"
  );
};

// --- Products Tab ---
const ProductsTab: React.FC<{
  searchTerm: string;
  filterCategory: string;
  filterStatus: string;
  vendors: Vendor[];
}> = ({ searchTerm, filterCategory, filterStatus, vendors }) => {
  const [products, setProducts] = useState<VendorProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [modal, setModal] = useState<
    "add" | "edit" | "pricing" | "delete" | null
  >(null);
  const [selectedProduct, setSelectedProduct] = useState<VendorProduct | null>(
    null,
  );
  const [isProcessing, setIsProcessing] = useState(false);

  // Which vendor a new product will be created under (create needs a vendorId)
  const [selectedVendorId, setSelectedVendorId] = useState("");

  const vendorMap = useMemo(() => {
    const map: Record<string, string> = {};
    vendors.forEach((v) => {
      map[v.id] = v.name;
    });
    return map;
  }, [vendors]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCategory, filterStatus]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllVendorProducts(currentPage, 10, {
        search: searchTerm || undefined,
        category: filterCategory || undefined,
        status: filterStatus || undefined,
      });
      setProducts(response.results || []);
      setTotalPages(response.pagination?.totalPages ?? 1);
    } catch (err) {
      setError("Failed to load products.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterCategory, filterStatus, refreshTrigger]);

  useEffect(() => {
    const timer = setTimeout(() => fetchProducts(), 400);
    return () => clearTimeout(timer);
  }, [fetchProducts]);

  const closeModal = () => {
    setModal(null);
    setSelectedProduct(null);
  };

  const handleAddProduct = async (formData: FormData) => {
    if (!selectedVendorId) {
      toast.error("Please select a vendor first.");
      return;
    }
    setIsProcessing(true);
    try {
      await createVendorProduct(selectedVendorId, formData);
      toast.success("Product created successfully!");
      closeModal();
      setRefreshTrigger((p) => p + 1);
    } catch (error) {
      console.error("Create Product Error:", error);
      toast.error(getApiErrorMessage(error, "Failed to create product."));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateProduct = async (updates: ProductUpdateData) => {
    if (!selectedProduct) return;
    setIsProcessing(true);
    try {
      await updateVendorProduct(selectedProduct.id, updates);
      toast.success("Product updated.");
      closeModal();
      setRefreshTrigger((p) => p + 1);
    } catch (err) {
      toast.error("Failed to update product details.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdatePricing = async (updates: PricingUpdateData) => {
    if (!selectedProduct) return;
    setIsProcessing(true);
    try {
      await updateProductPricing(selectedProduct.id, updates);
      toast.success("Pricing updated.");
      closeModal();
      setRefreshTrigger((p) => p + 1);
    } catch (err) {
      toast.error("Failed to update product pricing.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;
    setIsProcessing(true);
    try {
      await deleteVendorProduct(selectedProduct.id);
      toast.success("Product deleted.");
      closeModal();
      setRefreshTrigger((p) => p + 1);
    } catch (err) {
      toast.error("Failed to delete product.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggleFeatured = async (product: VendorProduct) => {
    setIsProcessing(true);
    try {
      await setProductFeatured(product.id, !product.isFeatured);
      toast.success(
        product.isFeatured ? "Product un-featured." : "Product featured.",
      );
      setRefreshTrigger((p) => p + 1);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Failed to update featured status.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) return <PageLoader message="Loading products..." />;
  if (error) return <div className="tab-error">{error}</div>;

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
          marginBottom: "16px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Select
            value={selectedVendorId}
            onChange={(e) => setSelectedVendorId(e.target.value)}
            style={{
              padding: "9px 12px",
              borderRadius: "8px",
              border: "1px solid #ddd",
              minWidth: "200px",
            }}
          >
            <option value="">Select vendor to add for…</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </Select>
          <button
            className="add-product-btn"
            style={{
              background: "#174078",
              color: "#fff",
              border: "none",
              padding: "10px 16px",
              borderRadius: "8px",
              cursor: "pointer",
            }}
            onClick={() => {
              if (!selectedVendorId) {
                toast.info("Pick a vendor first, then add a product.");
                return;
              }
              setModal("add");
            }}
          >
            + Add New Product
          </button>
        </div>
      </div>

      {products.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          No products found.
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table admin-data-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Vendor</th>
                <th>Category</th>
                <th>Price (NGN)</th>
                <th>Price (GBP)</th>
                <th>Qty</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id}>
                  <td data-label="Image">
                    <ProductImage
                      paths={product.image}
                      alt={product.name}
                      fallback={defaultProductImage}
                      className="product-image"
                    />
                  </td>
                  <td data-label="Name">{product.name}</td>
                  <td data-label="Vendor">
                    {vendorNameOf(product, vendorMap)}
                  </td>
                  <td data-label="Category">
                    {formatCategory(product.category)}
                  </td>
                  <td data-label="Price (NGN)">
                    {product.priceNGN > 0
                      ? `₦${Number(product.priceNGN).toLocaleString()}`
                      : product.pricing?.NGN
                        ? `₦${Number(product.pricing.NGN).toLocaleString()}`
                        : "-"}
                  </td>
                  <td data-label="Price (GBP)">
                    {product.priceGBP > 0
                      ? `£${Number(product.priceGBP).toLocaleString()}`
                      : product.pricing?.GBP
                        ? `£${Number(product.pricing.GBP).toLocaleString()}`
                        : "-"}
                  </td>
                  <td data-label="Qty">{product.quantity}</td>
                  <td data-label="Status">
                    <span
                      className={`status-badge status-${
                        normalizeProductStatusForApi(product.productStatus || "")
                      }`}
                    >
                      {formatProductStatus(product.productStatus)}
                    </span>
                  </td>
                  <td data-label="Actions">
                    <div className="table-action-buttons">
                      <button
                        type="button"
                        className={`table-action-btn ${
                          product.isFeatured ? "featured" : ""
                        }`}
                        title={
                          product.isFeatured
                            ? "Un-feature Product"
                            : "Feature Product"
                        }
                        aria-label="Toggle featured"
                        disabled={isProcessing}
                        onClick={() => handleToggleFeatured(product)}
                      >
                        <FiStar />
                      </button>
                      <button
                        type="button"
                        className="table-action-btn"
                        title="Update Pricing"
                        aria-label="Update pricing"
                        onClick={() => {
                          setSelectedProduct(product);
                          setModal("pricing");
                        }}
                      >
                        <FiTag />
                      </button>
                      <button
                        type="button"
                        className="table-action-btn"
                        title="Edit Details"
                        aria-label="Edit product"
                        onClick={() => {
                          setSelectedProduct(product);
                          setModal("edit");
                        }}
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        type="button"
                        className="table-action-btn danger"
                        title="Delete Product"
                        aria-label="Delete product"
                        onClick={() => {
                          setSelectedProduct(product);
                          setModal("delete");
                        }}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {products.length > 0 && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} loading={loading} />
      )}

      {modal === "add" && (
        <AddProductModal
          onClose={closeModal}
          onSave={handleAddProduct}
          isSaving={isProcessing}
        />
      )}
      {modal === "edit" && selectedProduct && (
        <EditProductModal
          product={selectedProduct}
          onClose={closeModal}
          onSave={handleUpdateProduct}
          isSaving={isProcessing}
        />
      )}
      {modal === "pricing" && selectedProduct && (
        <UpdatePricingModal
          product={selectedProduct}
          onClose={closeModal}
          onSave={handleUpdatePricing}
          isSaving={isProcessing}
        />
      )}
      {modal === "delete" && selectedProduct && (
        <DeleteConfirmationModal
          onClose={closeModal}
          onConfirm={handleDeleteProduct}
          isDeleting={isProcessing}
          itemName={selectedProduct.name}
        />
      )}
    </>
  );
};

// --- Orders Tab (pending backend endpoint) ---
const OrdersTab: React.FC = () => (
  <div
    style={{
      textAlign: "center",
      padding: "60px 20px",
      color: "#666",
    }}
  >
    <h3 style={{ marginBottom: "8px", color: "#333" }}>Orders coming soon</h3>
  </div>
);

// --- Main Page ---
const NaijaShopPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<NaijaShopTab>("products");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [vendors, setVendors] = useState<Vendor[]>([]);

  useEffect(() => {
    const loadVendors = async () => {
      try {
        const res = await getVendors(1, 100);
        setVendors(res.data || []);
      } catch (err) {
        console.error("Failed to load vendors:", err);
      }
    };
    loadVendors();
  }, []);

  // Reset search/filter when switching tabs
  useEffect(() => {
    setSearchTerm("");
    setFilterCategory("");
    setFilterStatus("");
  }, [activeTab]);

  return (
    <div className="products-page">

      <header className="page-header">
        <h3>Naija Shop</h3>
        <div className="page-controls">
          <PageToolbar>
            {activeTab === "products" && (
              <>
                <PageFilterDropdown
                  value={filterCategory}
                  options={CATEGORIES}
                  onChange={setFilterCategory}
                  allLabel="All categories"
                />
                <PageFilterDropdown
                  value={filterStatus}
                  options={PRODUCT_STATUS_OPTIONS}
                  onChange={setFilterStatus}
                  allLabel="All stock statuses"
                />
              </>
            )}
            <PageSearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={
                activeTab === "products"
                  ? "Search products..."
                  : "Search orders..."
              }
              disabled={activeTab === "orders"}
            />
          </PageToolbar>
        </div>
      </header>

      <div className="page-content-wrapper">
        <div className="product-tabs">
          <button
            className={activeTab === "products" ? "active" : ""}
            onClick={() => setActiveTab("products")}
          >
            Products
          </button>
          <button
            className={activeTab === "orders" ? "active" : ""}
            onClick={() => setActiveTab("orders")}
          >
            Orders
          </button>
        </div>

        <div className="product-content">
          {activeTab === "products" ? (
            <ProductsTab
              searchTerm={searchTerm}
              filterCategory={filterCategory}
              filterStatus={filterStatus}
              vendors={vendors}
            />
          ) : (
            <OrdersTab />
          )}
        </div>
      </div>
    </div>
  );
};

export default NaijaShopPage;
